// visualLayersApi — typed fetchers for GET /v1/sites/:id/visual-layers
// and GET /v1/map-captures?site_id=. Powers the Floor Stack 鳥瞰層 (PR-E)
// surface that lets operators jump from the multi-floor diamond into a
// satellite map of the whole site.
//
// docs/specs/spatial-hierarchy-v2.md §6.1 / §7

import { apiFetch } from "../../lib/api-client.js";

export type OutdoorQuality =
  | "none"               // no aerial layer at all
  | "satellite_capture"  // raw NLSC ortho stitch (the source_kind backend stores)
  | "satellite_hd"       // upgraded high-resolution pipeline output
  | "model_3d";          // 3D mesh (glTF / GLB); PR-F renders these

export interface VisualLayerRow {
  entity_type: "site" | "floor" | "building";
  entity_id: string;
  source_kind: string;
  width_px: number | null;
  height_px: number | null;
  model_format: string | null;
  created_at: string;
}

export interface VisualLayersResponse {
  ok: true;
  site_id: string;
  /** Highest-resolution kind currently present at site level — drives
   *  the badge text on the aerial tier card. */
  outdoor_quality: OutdoorQuality;
  site_layers: VisualLayerRow[];
  floor_layers: VisualLayerRow[];
  total_visual_assets: number;
  correlation_id: string | null;
}

export interface SatelliteCapture {
  id: string;
  layout_id: string;
  r2_key: string;
  width_px: number;
  height_px: number;
  capture_bbox_sw_lat: number;
  capture_bbox_sw_lon: number;
  capture_bbox_ne_lat: number;
  capture_bbox_ne_lon: number;
  capture_zoom: number;
  capture_tile_source: string;
  source_kind: string;
  model_format: string | null;
  created_at: string;
}

export interface CapturesResponse {
  ok: true;
  captures: SatelliteCapture[];
  correlation_id: string | null;
}

export class VisualLayersApiError extends Error {
  status: number; code: string; correlationId: string | null;
  constructor(status: number, code: string, correlationId: string | null) {
    super(code);
    this.status = status; this.code = code; this.correlationId = correlationId;
  }
}

async function asError(r: Response): Promise<VisualLayersApiError> {
  let code = "request_failed";
  let cid: string | null = r.headers.get("x-correlation-id");
  try {
    const b = (await r.json()) as { error?: string; correlation_id?: string };
    code = b.error ?? code; cid = b.correlation_id ?? cid;
  } catch { /* not JSON */ }
  return new VisualLayersApiError(r.status, code, cid);
}

export async function getVisualLayers(
  siteId: string,
  tenantId: string | null = null,
): Promise<VisualLayersResponse> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/sites/${encodeURIComponent(siteId)}/visual-layers${qs}`, {
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as VisualLayersResponse;
}

export async function getSiteCaptures(
  siteId: string,
  tenantId: string | null = null,
): Promise<SatelliteCapture[]> {
  const params = new URLSearchParams({ site_id: siteId });
  if (tenantId) params.set("tenant_id", tenantId);
  const r = await apiFetch(`/v1/map-captures?${params.toString()}`, {
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  const b = (await r.json()) as CapturesResponse;
  return b.captures;
}

export function friendlyVisualLayersError(e: unknown): string {
  if (!(e instanceof VisualLayersApiError)) {
    return e instanceof Error ? e.message : "鳥瞰層資料讀取失敗。";
  }
  switch (e.code) {
    case "missing_context":   return "找不到租戶 context — 請重新登入。";
    case "missing_permission":return "您沒有讀取此案場視覺資產的權限。";
    case "missing_site_id":   return "案場 id 缺失（內部錯誤）。";
    default:                  return `${e.code}（${e.correlationId ?? "—"}）`;
  }
}
